import { Autocomplete, Divider, Grid, TextField, Typography } from '@mui/material';
import React, { useContext, useEffect, useState } from 'react'
import Required from '../../../components/utility/required';
import GlobalButton from '../../../components/utility/globalButton/button';
import popupStyles from "../../../assets/styles/componentStyles/globalPopupStyles.module.scss"
import contactStyles from "../../../assets/styles/componentStyles/campaignContactStyles/campaignContactStyle.module.scss";
import globalStyles from "../../../assets/styles/global.module.scss";
import style from "../../../assets/styles/pageStyles/ruleBasedInbox/threadList/threadList.module.scss"
import { IntegrationContext, IntegrationContextPayload } from '../../../context/integrationContext';
import { countryCodesInterface, createNewThreadPayload, metaIntegrationPhoneNumberDataNew, metaIntegrationWhatsAppDataNew, newThreadInfo } from '../../../models/axiosModel';
import { UserContext, UserContextPayload } from '../../../context/userContext';
import { ContactContext, ContactContextPayload } from '../../../context/contactContext';
import { CountryCode, parsePhoneNumber } from 'libphonenumber-js/mobile';
import { isWhatsAppIntegration } from '../utils';
import RenderCustomOption from '../../../components/global/autocomplete/renderCustomOption';
import { filterAutocompleteOptionsWithException } from '../../../utils/constants';
import { addNewPhoneNumberObj } from '../constants';
import { useRuleBasedInboxStore } from '../../../store/ruleBasedInboxStore';
import { useShallow } from 'zustand/react/shallow';

interface AddNewThreadPopupProps {
    getSpecificChat: (chatbot_user_id: string | number, offSetValue: number, flagDebugParam: boolean, flagViewDetailedLogParam: boolean, flagGetThreadDetailByPhoneNumber?: boolean, phoneNumberInfo?: newThreadInfo | null, forceRefresh?: boolean) => Promise<void>
} 

const AddNewThreadPopup: React.FC<AddNewThreadPopupProps> = (props) => {
    
    const { getSpecificChat } = props;
    
    const { integrationListNewStructure } = useContext(IntegrationContext) as IntegrationContextPayload;
    const { setFlagLoading, handleError } = useContext(UserContext) as UserContextPayload;
    const { getCountryCodeApi, countryCodeList } = useContext(ContactContext) as ContactContextPayload;

    const { setFlagNewThreadPopup } = useRuleBasedInboxStore(useShallow((state) => ({
        setFlagNewThreadPopup: state.setFlagNewThreadPopup
    })))

    const [payload, setPayload] = useState<createNewThreadPayload>({
        integrated_channel_id: '',
        user_wa_phone_number: '',
        user_country_code: 'IN' // Default: India (+91)
    });
    const [flagInValidPhoneNumber, setFlagInValidPhoneNumber] = useState<boolean>(false);

    // Get Sender's Phone Number Options
    const getSenderPhoneNumberOptions = () => {
        const phoneNumberOptions: metaIntegrationPhoneNumberDataNew[] = [];
        if(integrationListNewStructure){
            integrationListNewStructure?.filter(isWhatsAppIntegration)?.map((whatsAppIntegration: metaIntegrationWhatsAppDataNew) => {
                if(whatsAppIntegration?.meta_integration_phone_number_data?.length > 0){
                    phoneNumberOptions.push(...whatsAppIntegration?.meta_integration_phone_number_data);
                }
            });
        }
        return [...phoneNumberOptions, addNewPhoneNumberObj];
    }

    // Get Country Code API
    const getCountryCodeApiCall = async () => {
        setFlagLoading((prev) => ({ ...prev, flagRoundLoader: true }));
        const res = await getCountryCodeApi();
        if(!(res.status)){
            handleError(res);   
        }
        setFlagLoading((prev) => ({ ...prev, flagRoundLoader: false }));
    } 

    useEffect(() => {
        if(!countryCodeList){
            getCountryCodeApiCall();
        }
    }, []);

    // Auto-select sender phone number when only one real option is available
    useEffect(() => {
        const phoneNumberList = getSenderPhoneNumberOptions().filter(
            (option) => option.meta_integration_whatsapp_phone_number_id !== addNewPhoneNumberObj.meta_integration_whatsapp_phone_number_id
        );

        if (phoneNumberList.length === 1) {
            setPayload((prev) => {
                if (prev.integrated_channel_id) return prev;
                return {
                    ...prev,
                    integrated_channel_id: String(phoneNumberList[0]?.integrated_channel_id ?? ''),
                };
            });
        }
    }, [integrationListNewStructure]);

    // Validate Phone Number
    const handleValidatePhoneNumber = (phoneNumber: string, countryCode: string) => {
        try {
            const validate = parsePhoneNumber(phoneNumber, countryCode as CountryCode);
            if(!validate.isValid() || validate.country != countryCode || !(validate.getType()?.toLowerCase() == "undefined" ||  validate.getType()?.toLowerCase().indexOf('mobile') != -1 )){
                setFlagInValidPhoneNumber(true);
            } else {
                setFlagInValidPhoneNumber(false);
            }
        } catch (err) {
            setFlagInValidPhoneNumber(true);
        }
    } 

    // on Change Of Recipient's phone number and Recipient's country code
    const onChangeOfRecipientInfo = (recipientPhoneNumber: string, recipientCountryCode: string) => {
        if(recipientPhoneNumber && recipientCountryCode){
            handleValidatePhoneNumber(recipientPhoneNumber, recipientCountryCode);
        } else {
            setFlagInValidPhoneNumber(false);
        }
    } 

    // Check Disable Create button
    const checkDisableCreateButton = () => {
        if(!payload?.integrated_channel_id || !payload?.user_country_code || !payload?.user_wa_phone_number) return true;
        
        try {
            const validate = parsePhoneNumber(payload?.user_wa_phone_number, payload?.user_country_code as CountryCode);
            if(validate.isValid() && validate.country == payload?.user_country_code && (validate.getType()?.toLowerCase() == "undefined" ||  validate.getType()?.toLowerCase().indexOf('mobile') != -1 )){
                if(flagInValidPhoneNumber) setFlagInValidPhoneNumber(false);
                return false;
            } else return true;
        } catch (err) {
            return true;
        }
    }

    // Get Formatted Phone Number and Integrated Channel ID(Sender's Phone Number)
    const getUpdatedPayload = () => {
        let updatedPayload: newThreadInfo = {
            integrated_channel_id: payload?.integrated_channel_id,
            user_wa_phone_number: payload?.user_wa_phone_number
        }
        try {
            const validate = parsePhoneNumber(updatedPayload?.user_wa_phone_number, payload?.user_country_code as CountryCode);
            if(validate.isValid()){
                return {
                    ...updatedPayload,
                    user_wa_phone_number: validate.formatInternational().replace(/\D/g, ''),
                }
            }
            return updatedPayload;
        } catch(err) {
            return updatedPayload;
        }
    }

    // On Click Of Create Thread Conversation.
    const onClickOfCreateConversationBtn = async () => {
        const updatedPayload: newThreadInfo = getUpdatedPayload();
        setFlagNewThreadPopup(false);
        await getSpecificChat("", 0, false, false, true, updatedPayload);
    } 

    // Get Field Value function
    const getFieldValue = (field: keyof createNewThreadPayload) => {
        switch(field){
            case "integrated_channel_id":
                const phoneNumberOptions = getSenderPhoneNumberOptions();
                return phoneNumberOptions?.find((phoneNumber) => String(phoneNumber?.integrated_channel_id) === payload?.integrated_channel_id) ?? null;
            case "user_country_code":
                return countryCodeList?.find((code: countryCodesInterface) => code.country_iso_code === payload?.user_country_code) || null;
            default: 
                return payload[field] ?? '';
        }
    }

    // On Change Handle of Field
    const onChangeOfField = <K extends keyof createNewThreadPayload>(field: K, value: createNewThreadPayload[K]) => {
        setPayload((prev) => ({
            ...prev,
            [field]: value
        }));
    }

    return (
        <>
            <Grid xs={12}>
                <Grid container direction={"column"} justifyContent={"center"}>
                    <Grid item textAlign={"center"}>
                        <Typography className={popupStyles.popupTitleTypography}>
                            Create New Conversation
                        </Typography>
                    </Grid>

                    <Divider className={popupStyles.popupTitleDivider} />

                    <Grid item>
                        <Grid container className={popupStyles.popupBodyGridContainer} gap={3}>
                            {/*  Sender's Phone Number */}
                            <Grid item xs={12}>
                                <Grid container gap={"12px"}>
                                    <Grid item xs={12}>
                                        <Typography className={popupStyles.popupFieldTypography}>
                                            Sender's Phone Number <Required />
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Autocomplete
                                            className={`${contactStyles.contactFilterDropdownsInPopup}`}
                                            options={getSenderPhoneNumberOptions()}
                                            getOptionLabel={(option: metaIntegrationPhoneNumberDataNew) => `${option?.display_phone_number} (${option?.verified_name})`}
                                            value={getFieldValue('integrated_channel_id') as metaIntegrationPhoneNumberDataNew | null}
                                            onChange={(_, value: metaIntegrationPhoneNumberDataNew | null) => 
                                                onChangeOfField('integrated_channel_id', String(value?.integrated_channel_id) ?? '')
                                            }
                                            ListboxProps={{
                                                className: style.autocompleteListbox,
                                            }}
                                            isOptionEqualToValue={(option, value) =>
                                                option.meta_integration_whatsapp_phone_number_id === value.meta_integration_whatsapp_phone_number_id
                                            }
                                            filterOptions={(options, state) => filterAutocompleteOptionsWithException(options, state, ["display_phone_number", "verified_name"], "meta_integration_whatsapp_phone_number_id")}
                                            renderOption={(props: React.HTMLAttributes<HTMLLIElement>, option: metaIntegrationPhoneNumberDataNew) => {
                                                return (
                                                    <RenderCustomOption
                                                        autocompleteRenderOptionProps={props} 
                                                        option={option} 
                                                        customOptionName={"New WhatsApp Business Account"}
                                                        optionName={`${option?.display_phone_number} (${option?.verified_name})`}
                                                        optionLabelKey={'meta_integration_whatsapp_phone_number_id'}
                                                        onClickOfCustomOption={() => window.open(`/channels/viewintegration/whatsapp`, "_blank")}
                                                    />
                                                );
                                            }}
                                            renderInput={(params) => <TextField {...params} placeholder="Select Phone Number" />}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>

                            {/*  Recipient's Country Code */}
                            <Grid item xs={12}>
                                <Grid container gap={"12px"}>
                                    <Grid item xs={12}>
                                        <Typography className={popupStyles.popupFieldTypography}>
                                            Recipient's Country Code <Required />
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Autocomplete
                                            className={`${contactStyles.contactFilterDropdownsInPopup}`}
                                            options={countryCodeList || []}
                                            getOptionLabel={(option: countryCodesInterface) => `${option.country_name} (+${option?.country_code})`}
                                            value={getFieldValue('user_country_code') as countryCodesInterface | null}
                                            onChange={(_, value: countryCodesInterface | null) => {
                                                onChangeOfField('user_country_code', value?.country_iso_code ?? '');
                                                onChangeOfRecipientInfo(payload?.user_wa_phone_number, value?.country_iso_code ?? '');
                                            }}
                                            ListboxProps={{
                                                className: style.autocompleteListbox,
                                            }}
                                            renderInput={(params) => <TextField {...params} placeholder="Select Country Code" />}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid> 

                            {/* Recipient's Phone Number */}
                            <Grid item xs={12}>
                                <Grid container gap={"12px"}>
                                    <Grid item xs={12}>
                                        <Typography className={popupStyles.popupFieldTypography}>
                                            Recipient's Phone Number <Required />
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            value={getFieldValue('user_wa_phone_number')}
                                            onChange={(e) => {
                                                onChangeOfField('user_wa_phone_number', e.target.value);
                                                onChangeOfRecipientInfo(e.target.value, payload?.user_country_code);
                                            }}
                                            placeholder={`Enter Phone Number`}
                                            className={`${contactStyles.campaignContactPopupTextField} ${globalStyles.fullwidth}`}
                                        />
                                        {flagInValidPhoneNumber && <span className={style.inValidPhoneNumber}>Please Enter Valid Phone Number</span>}
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>

                    <Grid item>
                        <Grid container justifyContent={"center"} alignItems={"center"} gap={"12px"} sx={{ padding: "20px 0" }}>
                            <Grid item>
                                <GlobalButton
                                    buttonText="Cancel"
                                    sx={{ width: "130px", padding: "6.5px 20px !important", fontWeight: "500 !important" }}
                                    className='borderedButtonStyle'
                                    onClick={() => setFlagNewThreadPopup(false)}
                                />
                            </Grid>
                            <Grid item>
                                <GlobalButton
                                    buttonText="Create"
                                    sx={{ width: "130px", padding: "6.5px 20px !important", fontWeight: "500 !important" }}
                                    className='primaryButtonStyle'
                                    disabled={checkDisableCreateButton()}
                                    onClick={() => onClickOfCreateConversationBtn()}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </>
    )
}

export default AddNewThreadPopup
